import { useMemo } from "react";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useBillingStatus, useForgeEntitlement } from "@/hooks/usePlatformBilling";
import { useModuleToggles } from "@/hooks/useModuleToggles";
import { useRuntimeFlags } from "@/contexts/FeatureFlagContext";
import { resolveCapability } from "@/lib/capabilities/resolve";
import { CAPABILITIES } from "@/lib/capabilities/registry";
import type { CapabilityResult, ResolveContext } from "@/lib/capabilities/types";
import type { SubscriptionTier } from "@/lib/tier-config";

interface ResolvedCapabilityContext {
  context: ResolveContext;
  isLoading: boolean;
}

export interface CapabilityHookResult extends CapabilityResult {
  isLoading: boolean;
}

function useResolvedCapabilityContext(): ResolvedCapabilityContext {
  const { roles, isLoading: rolesLoading } = useUserRoles();
  const { effectiveTier } = useBillingStatus();
  const { isLoading: entitlementLoading } = useForgeEntitlement();
  const { toggles, isLoading: togglesLoading } = useModuleToggles();
  const { isEnabled, flags } = useRuntimeFlags();

  const context = useMemo<ResolveContext>(() => {
    // Combined toggle map: existing module toggles + registry defaults for sub-features.
    const combined: Record<string, boolean> = { ...toggles };
    for (const cap of Object.values(CAPABILITIES)) {
      if (cap.toggle && combined[cap.key] === undefined) {
        combined[cap.key] = cap.toggle.default;
      }
    }
    return {
      roles,
      tier: (effectiveTier || "free") as SubscriptionTier,
      toggles: combined,
      // A kill-switch flag is "killed" when it EXISTS as a flag AND is disabled.
      isFlagKilled: (flagKey: string) => flagKey in flags && !isEnabled(flagKey),
    };
  }, [roles, effectiveTier, toggles, flags, isEnabled]);

  return {
    context,
    isLoading: Boolean(rolesLoading || entitlementLoading || togglesLoading),
  };
}

export function useResolveContext(): ResolveContext {
  return useResolvedCapabilityContext().context;
}

/**
 * Like {@link useResolveContext}, but also surfaces whether the underlying
 * billing/roles/toggle state is still loading. Consumers that gate UI on the
 * resolved context (e.g. nav filtering) should defer until `isLoading` is
 * false to avoid hiding-then-popping entries while defaults (tier `"free"`)
 * are in effect. The existing `useResolveContext()` signature is unchanged.
 */
export function useResolveContextWithLoading(): { ctx: ResolveContext; isLoading: boolean } {
  const { context, isLoading } = useResolvedCapabilityContext();
  return { ctx: context, isLoading };
}

export function useCapability(key: string): CapabilityHookResult {
  const { context, isLoading } = useResolvedCapabilityContext();
  return useMemo(() => ({ ...resolveCapability(key, context), isLoading }), [key, context, isLoading]);
}
